import { useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import calendarData from "@/data/calendarBoard.json";
import { useBoard } from "@/hooks/useBoard";
import { CalendarClient } from "./CalendarClient";
import { Card } from "@/design/primitives";
import { AuthGate } from "@/screens/cc";


const BRAND_SCOPE_MAP = {
  tjb: { slug: "truejoybirthing", name: "True Joy Birthing" },
  at: { slug: "agentictrust", name: "AgenticTrust" },
  to: { slug: "trustoffice", name: "TrustOffice" },
  av: { slug: "aeriusview", name: "AeriusView" },
  wp: { slug: "wingpoint", name: "WingPoint" },
  tm: { slug: "trustminutes", name: "TrustMinutes" },
  sv: { slug: "socializevideo", name: "SocializeVideo" },
  sd: { slug: "stenodesk", name: "StenoDesk" },
};

// Calendar page — three-pane client (rail / list / reader), mail-client standard.
// Also embeddable bare inside the consolidated Schedule page's Calendar tab
// (Jeff 2026-10-21): `bare` renders just the client (no page container/testid,
// no useBoard hook — the caller owns data) so one client serves both pages.
export default function CalendarBoardPage({ brandScope, bare = false }) {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const board = useBoard("calendar");
  const [dismissed, setDismissed] = useState(false);

  const scopeInfo = brandScope ? BRAND_SCOPE_MAP[brandScope] : null;

  const liveItems = useMemo(() => (board.items || []), [board.items]);
  const bakedItems = useMemo(
    () => (calendarData.cards || []).map((c) => ({ ...c, payload: c.payload || {} })),
    [],
  );
  const viewItemsRaw = board.live && liveItems.length ? liveItems : bakedItems;
  const viewMeta = board.live && board.meta ? board.meta : calendarData.stamp;

  // Scope items when brandScope is set. Structural rows (rail-*) are not brand-tagged
  // data: keep the matching rail-brand row (payload.targetBrand) so the scoped rail
  // still renders, plus rail-state/rail-next are hidden by the client in scoped mode.
  const viewItems = useMemo(() => {
    if (!scopeInfo) return viewItemsRaw;
    return viewItemsRaw.filter((i) => {
      if (i.kind === "rail-brand") return i.payload?.targetBrand === scopeInfo.name;
      if (i.kind?.startsWith("rail")) return i.payload?.brand === scopeInfo.name;
      // calendar items carry brand at payload.brand (display name)
      return i.payload?.brand === scopeInfo.name;
    });
  }, [viewItemsRaw, scopeInfo]);

  // Bare mode (embedded in the consolidated Schedule page): just the client,
  // no page container — the Schedule page owns the tab bar, spacing, testids.
  // Full mode: the original three-pane page at /calendar (still routed for
  // brand-scoped variants /{brand}/calendar).
  if (bare) {
    return (
      <>
        {board.live && !dismissed && !board.meta?.ingested_at && (
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="self-start rounded-control border border-os-border px-3 py-1.5 text-left font-mono text-[0.6875rem] text-os-ink-3 hover:text-os-ink"
          >
            Today this window is a hand-built sample — Kit's live calendar sync arrives on the first fetch. Tap to dismiss.
          </button>
        )}
        {!board.live && (
          <Card quiet className="px-5 py-3">
            <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
              Kit's calendar feed is offline this visit — the page shows the last committed window.
            </p>
          </Card>
        )}
        <CalendarClient items={viewItems} meta={viewMeta} board={board} variant={desktop ? "desktop" : "mobile"} brandScope={scopeInfo} />
        {board.notice?.text && (
          <p className="px-1 text-[0.8125rem] text-os-amber">{board.notice.text}</p>
        )}
      </>
    );
  }

  return (
    <div data-testid="calendar-page" className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        {board.status === "needs-signin" ? (
          <>
            <AuthGate />
            <CalendarClient items={scopeInfo ? viewItems : bakedItems} meta={calendarData.stamp} board={board} variant={desktop ? "desktop" : "mobile"} brandScope={scopeInfo} />
          </>
        ) : (
          <>
            {board.live && !dismissed && !board.meta?.ingested_at && (
              <button
                type="button"
                data-testid="calendar-sample-note"
                onClick={() => setDismissed(true)}
                className="self-start rounded-control border border-os-border px-3 py-1.5 text-left font-mono text-[0.6875rem] text-os-ink-3 hover:text-os-ink"
              >
                Today this window is a hand-built sample — Kit's live calendar sync arrives on the first fetch. Tap to dismiss.
              </button>
            )}
            {!board.live && (
              <Card quiet className="px-5 py-3">
                <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
                  Kit's calendar feed is offline this visit — the page shows the last committed window.
                </p>
              </Card>
            )}
            <CalendarClient items={viewItems} meta={viewMeta} board={board} variant={desktop ? "desktop" : "mobile"} brandScope={scopeInfo} />
            {board.notice?.text && (
              <p className="px-1 text-[0.8125rem] text-os-amber" data-testid="calendar-notice">{board.notice.text}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
